// T2T-05 — EmbeddedChat
// C3-UI shipped manual refresh; C4 layers per-request SSE on top so messages,
// edits, deletes, typing, and read receipts arrive live.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  fetchMessages,
  sendMessage,
  editMessage,
  deleteMessage,
  markMessagesRead,
  presignAttachment,
  registerAttachment,
  uploadFileToS3,
  fetchAttachmentDownloadUrl,
  fetchMentionable,
  sendTypingPing,
  fetchMuteStatus,
  muteThread,
  unmuteThread,
  buildChatPdfExportUrl,
} from "../../../../../api/t2tMessagesApi";
import MessageList         from "./MessageList";
import MessageInputBar     from "./MessageInputBar";
import TypingIndicator     from "./TypingIndicator";
import ConnectionBanner    from "./ConnectionBanner";
import VendorInvitePanel   from "./VendorInvitePanel";
import useChatSse          from "./useChatSse";

const FETCH_LIMIT = 50;
const TYPING_DEBOUNCE_MS = 1000;

export default function EmbeddedChat({ requestId }) {
  const currentUser = useSelector((state) => state?.user?.users?.[0]);
  const currentUserId = currentUser?.user_id;
  // T2T-05-C6: only admins see the vendor-invite panel
  const isAdmin = !!(currentUser?.t2t_role_name || "").toLowerCase().includes("admin");

  const [thread,       setThread]       = useState(null);
  const [messages,     setMessages]     = useState([]);
  const [readBy,       setReadBy]       = useState([]); // [{ user_id, user_name, read_at }] for last visible message
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [notParticipant, setNotParticipant] = useState(false);
  const [hasMore,      setHasMore]      = useState(false);
  const [olderLoading, setOlderLoading] = useState(false);
  const [sending,      setSending]      = useState(false);
  const [typingUsers,  setTypingUsers]  = useState([]); // [{ user_id, user_name, expires }]
  // T2T-05-C7: per-thread mute + overflow menu
  const [muted,         setMuted]         = useState(false);
  const [muteBusy,      setMuteBusy]      = useState(false);
  const [menuOpen,      setMenuOpen]      = useState(false);
  const menuRef = useRef(null);
  const scrollRef     = useRef(null);
  const stickyBottom  = useRef(true);
  const lastTypingPingRef = useRef(0);
  const messagesRef   = useRef([]);
  messagesRef.current = messages;

  const onScroll = useCallback((e) => {
    const el = e.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickyBottom.current = distanceFromBottom < 40;
  }, []);

  // ─── Initial load ──────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError(null);
    setNotParticipant(false);
    const resp = await fetchMessages(requestId, { limit: FETCH_LIMIT });
    if (resp?.error) {
      // 403 from participant check → friendly locked state, not an error toast.
      // Different shapes: axios error in resp.error.response.status, fetch in resp.error.status.
      const code = resp.error?.status
                || resp.error?.response?.status
                || resp.status
                || resp.error?.statusCode;
      if (code === 403) setNotParticipant(true);
      else setError("Could not load messages.");
      setLoading(false);
      return;
    }
    const payload = resp?.data?.data || {};
    setThread(payload.thread || null);
    setMessages(payload.messages || []);
    setReadBy(payload.read_by || []);
    setHasMore(!!payload.has_more);
    setLoading(false);
    markMessagesRead(requestId).catch(() => {});
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  // T2T-05-C7: hydrate mute status on thread load
  useEffect(() => {
    if (!requestId) return;
    let cancelled = false;
    fetchMuteStatus(requestId).then((resp) => {
      if (cancelled) return;
      if (!resp?.error) setMuted(!!resp?.data?.data?.muted);
    });
    return () => { cancelled = true; };
  }, [requestId]);

  // Close overflow menu on outside click / Esc
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  const toggleMute = useCallback(async () => {
    if (muteBusy) return;
    setMuteBusy(true);
    const resp = muted ? await unmuteThread(requestId) : await muteThread(requestId);
    if (!resp?.error) {
      setMuted(!muted);
    } else {
      setError(resp.error?.message || "Could not update mute setting.");
    }
    setMuteBusy(false);
  }, [muted, muteBusy, requestId]);

  const handleExportPdf = useCallback(() => {
    setMenuOpen(false);
    const url = buildChatPdfExportUrl(requestId);
    window.open(url, "_blank", "noopener");
  }, [requestId]);

  useEffect(() => {
    if (!scrollRef.current) return;
    if (stickyBottom.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // ─── Load older ────────────────────────────────────────────────────────
  const loadOlder = useCallback(async () => {
    if (!hasMore || olderLoading || messages.length === 0) return;
    setOlderLoading(true);
    const oldestId = messages[0].message_id;
    const resp = await fetchMessages(requestId, { limit: FETCH_LIMIT, before_id: oldestId });
    if (resp?.error) { setOlderLoading(false); return; }
    const payload = resp?.data?.data || {};
    const older = payload.messages || [];
    if (older.length) {
      const el = scrollRef.current;
      const prevHeight = el ? el.scrollHeight : 0;
      setMessages((prev) => [...older, ...prev]);
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - prevHeight;
      });
    }
    setHasMore(!!payload.has_more);
    setOlderLoading(false);
  }, [hasMore, olderLoading, messages, requestId]);

  // ─── Send ──────────────────────────────────────────────────────────────
  const handleSend = useCallback(async ({ text, mentioned_user_ids, files }) => {
    if (sending) return;
    setSending(true);
    const attachment_ids = [];
    try {
      for (const file of files || []) {
        const presignResp = await presignAttachment(requestId, {
          filename:  file.name,
          mime_type: file.type || "application/octet-stream",
          file_size: file.size,
        });
        if (presignResp?.error) throw new Error(presignResp.error.message || "Pre-sign failed");
        const { upload_url, file_key } = presignResp.data?.data || {};
        if (!upload_url || !file_key) throw new Error("Missing upload URL");
        await uploadFileToS3(upload_url, file, file.type || "application/octet-stream");
        const regResp = await registerAttachment(requestId, {
          filename:  file.name,
          mime_type: file.type || "application/octet-stream",
          file_size: file.size,
          file_key,
        });
        if (regResp?.error) throw new Error(regResp.error.message || "Register failed");
        const att = regResp.data?.data;
        if (att?.attachment_id) attachment_ids.push(att.attachment_id);
      }
    } catch (e) {
      setSending(false);
      setError(e.message || "Could not upload attachment.");
      return;
    }
    const body = {
      message_text: text || "",
      mentioned_user_ids: (mentioned_user_ids || []).map(Number).filter(Boolean),
      attachment_ids,
    };
    const resp = await sendMessage(requestId, body);
    if (resp?.error) {
      setError(resp.error.message || "Could not send message.");
      setSending(false);
      return;
    }
    const created = resp.data?.data;
    if (created) {
      stickyBottom.current = true;
      setMessages((prev) => [...prev, created]);
      // Sender stops typing implicitly
      setTypingUsers((prev) => prev.filter(t => t.user_id !== currentUserId));
    }
    setSending(false);
  }, [requestId, sending, currentUserId]);

  // ─── Edit / Delete ─────────────────────────────────────────────────────
  const handleEdit = useCallback(async (messageId, newText) => {
    const resp = await editMessage(requestId, messageId, { message_text: newText });
    if (resp?.error) { setError(resp.error.message || "Could not edit."); return; }
    const updated = resp.data?.data;
    setMessages((prev) => prev.map(m => m.message_id === messageId ? { ...m, ...updated } : m));
  }, [requestId]);

  const handleDelete = useCallback(async (messageId) => {
    const resp = await deleteMessage(requestId, messageId);
    if (resp?.error) { setError(resp.error.message || "Could not delete."); return; }
    setMessages((prev) => prev.map(m =>
      m.message_id === messageId ? { ...m, is_deleted: 1, deleted_at: new Date().toISOString() } : m
    ));
  }, [requestId]);

  const queryMentions = useCallback(async (q) => {
    const resp = await fetchMentionable(requestId, q, 20);
    if (resp?.error) return [];
    return resp.data?.data?.rows || [];
  }, [requestId]);

  const openAttachment = useCallback(async (attachmentId) => {
    const resp = await fetchAttachmentDownloadUrl(attachmentId);
    if (resp?.error) { setError(resp.error.message || "Could not get download link."); return; }
    const { download_url } = resp.data?.data || {};
    if (download_url) window.open(download_url, "_blank", "noopener");
  }, []);

  // ─── Typing keystroke debounce (1 ping/sec, fire-and-forget) ───────────
  const handleKeystroke = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingPingRef.current < TYPING_DEBOUNCE_MS) return;
    lastTypingPingRef.current = now;
    sendTypingPing(requestId).catch(() => {});
  }, [requestId]);

  // ─── SSE handlers ──────────────────────────────────────────────────────
  const onMessageNew = useCallback((msg) => {
    if (!msg?.message_id) return;
    setMessages((prev) => {
      if (prev.some(m => m.message_id === msg.message_id)) return prev;
      return [...prev, msg];
    });
    // mark-read so others see read receipts move
    markMessagesRead(requestId).catch(() => {});
  }, [requestId]);

  const onMessageEdited = useCallback((evt) => {
    setMessages((prev) => prev.map(m =>
      m.message_id === evt.message_id
        ? { ...m, message_text: evt.new_text, edited_at: evt.edited_at }
        : m
    ));
  }, []);

  const onMessageDeleted = useCallback((evt) => {
    setMessages((prev) => prev.map(m =>
      m.message_id === evt.message_id
        ? { ...m, is_deleted: 1, deleted_at: new Date().toISOString() }
        : m
    ));
  }, []);

  const onParticipantRead = useCallback((evt) => {
    // evt: { user_id, user_name, message_id, read_at }
    const lastMsg = messagesRef.current[messagesRef.current.length - 1];
    if (!lastMsg || evt.message_id < lastMsg.message_id) return;
    setReadBy((prev) => {
      const filtered = prev.filter(p => Number(p.user_id) !== Number(evt.user_id));
      return [...filtered, { user_id: evt.user_id, user_name: evt.user_name, read_at: evt.read_at }];
    });
  }, []);

  const onUserTyping = useCallback((evt) => {
    if (!evt?.user_id || Number(evt.user_id) === Number(currentUserId)) return;
    const expires = evt.expires_at ? new Date(evt.expires_at).getTime() : (Date.now() + 3000);
    setTypingUsers((prev) => {
      const filtered = prev.filter(t => t.user_id !== evt.user_id);
      return [...filtered, { user_id: evt.user_id, user_name: evt.user_name, expires }];
    });
  }, [currentUserId]);

  const onUserStoppedTyping = useCallback((evt) => {
    if (!evt?.user_id) return;
    setTypingUsers((prev) => prev.filter(t => Number(t.user_id) !== Number(evt.user_id)));
  }, []);

  // GC stale typing entries every second
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      setTypingUsers((prev) => prev.filter(x => x.expires > now));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const getLastMessageId = useCallback(() => {
    const arr = messagesRef.current;
    return arr.length ? arr[arr.length - 1].message_id : null;
  }, []);

  // Only subscribe AFTER the initial REST load has succeeded — otherwise SSE
  // fires before we know whether this user is a participant, and a 403 races
  // the friendly empty state into showing the red retry banner.
  const sseRequestId = (!notParticipant && thread && !loading) ? requestId : null;
  const { status: sseStatus } = useChatSse(sseRequestId, {
    onMessageNew,
    onMessageEdited,
    onMessageDeleted,
    onParticipantRead,
    onUserTyping,
    onUserStoppedTyping,
    getLastMessageId,
  });

  const readOnly = !!thread?.read_only;
  const dismissError = () => setError(null);
  const visibleMessages = useMemo(() => messages, [messages]);
  const typingNames = useMemo(
    () => typingUsers.map(t => t.user_name).filter(Boolean),
    [typingUsers]
  );

  // Read-receipts row — names of others (excluding current user) who have
  // read up to the last visible message.
  const readByOthers = useMemo(
    () => readBy.filter(r => Number(r.user_id) !== Number(currentUserId)),
    [readBy, currentUserId]
  );

  if (notParticipant) {
    return (
      <div
        className="t2t-chat"
        style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", minHeight: 320, textAlign: "center", padding: "40px 16px",
        }}
      >
        <i className="bi bi-lock" style={{ fontSize: 36, color: "var(--t2t-border)", marginBottom: 12 }} />
        <p style={{ fontWeight: 600, fontSize: 14, color: "var(--t2t-text-secondary)", marginBottom: 4 }}>
          You&rsquo;re not part of this conversation
        </p>
        <p style={{ fontSize: 12, color: "var(--t2t-text-muted)", maxWidth: 280, margin: 0 }}>
          Only the request initiator, current approvers, and people @mentioned in the thread can see and post messages.
        </p>
      </div>
    );
  }

  return (
    <div className="t2t-chat" style={{ display: "flex", flexDirection: "column", minHeight: 320 }}>
      {error && (
        <div
          role="alert"
          style={{
            background: "#FEE2E2", color: "#DC2626", border: "1px solid #FECACA",
            borderRadius: "var(--t2t-radius)", padding: "8px 12px", fontSize: 12,
            display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
          }}
        >
          <i className="bi bi-exclamation-triangle-fill" />
          <span style={{ flex: 1 }}>{error}</span>
          <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={dismissError} style={{ color: "#DC2626" }}>
            <i className="bi bi-x" />
          </button>
        </div>
      )}

      {/* T2T-05-C7: chat header with mute toggle + overflow (PDF export) */}
      <div
        style={{
          display: "flex", alignItems: "center", justifyContent: "flex-end",
          gap: 4, padding: "0 0 6px",
        }}
      >
        <button
          type="button"
          className="t2t-btn t2t-btn-ghost t2t-btn-sm"
          onClick={toggleMute}
          disabled={muteBusy}
          title={muted ? "Unmute this conversation" : "Mute this conversation"}
          aria-pressed={muted}
          style={muted ? { color: "var(--t2t-warning, #B45309)" } : undefined}
        >
          <i className={muted ? "bi bi-bell-slash" : "bi bi-bell"} />
          <span style={{ marginLeft: 6, fontSize: 12 }}>{muted ? "Muted" : "Notify"}</span>
        </button>

        <div ref={menuRef} style={{ position: "relative" }}>
          <button
            type="button"
            className="t2t-btn t2t-btn-ghost t2t-btn-sm"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title="More actions"
          >
            <i className="bi bi-three-dots-vertical" />
          </button>
          {menuOpen && (
            <div
              role="menu"
              style={{
                position: "absolute", right: 0, top: "100%", marginTop: 4,
                background: "white", border: "1px solid var(--t2t-border)",
                borderRadius: "var(--t2t-radius)", boxShadow: "var(--t2t-shadow-md)",
                minWidth: 180, zIndex: 30, overflow: "hidden",
              }}
            >
              {isAdmin ? (
                <button
                  type="button"
                  className="t2t-menu-item"
                  role="menuitem"
                  onClick={handleExportPdf}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, width: "100%",
                    padding: "8px 12px", background: "none", border: 0, cursor: "pointer",
                    textAlign: "left", fontSize: 13, color: "var(--t2t-text-primary)",
                  }}
                >
                  <i className="bi bi-file-earmark-pdf" />
                  <span>Export as PDF</span>
                </button>
              ) : (
                <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--t2t-text-muted)" }}>
                  No actions available
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConnectionBanner status={sseStatus} onRefreshThread={load} />

      {/* T2T-05-C6: vendor invitations — admin-only collapsible panel */}
      {isAdmin && <VendorInvitePanel requestId={requestId} />}

      {readOnly && (
        <div
          style={{
            background: "#F1F5F9", color: "var(--t2t-text-secondary)", border: "1px solid var(--t2t-border)",
            borderRadius: "var(--t2t-radius)", padding: "8px 12px", fontSize: 12,
            display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
          }}
        >
          <i className="bi bi-lock" />
          <span>This request is complete. The conversation is read-only.</span>
        </div>
      )}

      <div
        ref={scrollRef}
        onScroll={onScroll}
        style={{
          flex: 1, overflowY: "auto",
          padding: "4px 0 8px",
          minHeight: 240, maxHeight: "calc(100vh - 360px)",
        }}
      >
        {loading ? (
          <div>
            {[80, 60, 90, 70].map((w, i) => (
              <div key={i} className="kb-skeleton-bar" style={{ width: `${w}%`, height: 14, marginBottom: 12, borderRadius: 6 }} />
            ))}
          </div>
        ) : (
          <>
            <MessageList
              messages={visibleMessages}
              currentUserId={currentUserId}
              onLoadOlder={loadOlder}
              hasMore={hasMore}
              loadingOlder={olderLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onOpenAttachment={openAttachment}
              onRefresh={load}
            />
            {readByOthers.length > 0 && messages.length > 0 && (
              <div
                title={readByOthers.map(r => r.user_name).join(", ")}
                style={{
                  fontSize: 10, color: "var(--t2t-text-muted)",
                  textAlign: "right", padding: "2px 4px 0",
                }}
              >
                <i className="bi bi-check2-all" style={{ marginRight: 4 }} />
                {readByOthers.length > 5
                  ? `Read by ${readByOthers.length} people`
                  : (
                    <>
                      Read by {readByOthers.map(r => r.user_name).slice(0, 3).join(", ")}
                      {readByOthers.length > 3 ? ` +${readByOthers.length - 3} more` : ""}
                    </>
                  )}
              </div>
            )}
          </>
        )}
      </div>

      <TypingIndicator names={typingNames} />

      {!readOnly && (
        <MessageInputBar
          disabled={sending}
          onSend={handleSend}
          queryMentions={queryMentions}
          onKeystroke={handleKeystroke}
        />
      )}
    </div>
  );
}

EmbeddedChat.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
